import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  createApi,
  fetchBaseQuery,
} from '@reduxjs/toolkit/query/react';
import { ResponseBody } from '../types/common';
import {
  Account,
  Credentials,
  loginResponse,
  SignupFormList,
  ValidationResult,
} from '../types/login';
import { RootState } from './store';

import { setCredentials } from '../features/authSlice';
import { isOfType } from '../utils/typeGuard';
import {
  getTokens,
  setToken,
  deleteAllTokens,
  getLocalStorage,
  clearLocalStorage,
  getAccessToken,
} from '../utils/storage';
import {
  VITE_API_SERVER,
  VITE_API_V3,
  VITE_BASE_PUBLIC_PATH,
  VITE_BASE_URL,
} from '../utils/util';
import i18n from '../locales/i18n';

const rawBaseQuery = fetchBaseQuery({
  baseUrl: VITE_BASE_URL,
  prepareHeaders: (headers, { getState }) => {
    const accessToken = (getState() as RootState).auth.accessToken;
    const refreshToken = (getState() as RootState).auth.refreshToken;

    headers.set('Accept-Language', i18n.language);
    if (accessToken) {
      const sessionAccessToken = getAccessToken();
      headers.set('Authorization', `Bearer ${accessToken}`);

      if (!sessionAccessToken || sessionAccessToken !== accessToken) {
        setToken({ accessToken, refreshToken });
      }
    } else {
      headers.set('Authorization', `Bearer ${getAccessToken()}`);
    }
    return headers;
  },
});

const dynamicBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const urlEnd = typeof args === 'string' ? args : args.url;
  const isAuth = typeof args !== 'string' && args.url.includes('auth');
  const isV3 =
    typeof args === 'string'
      ? args.includes('library') ||
        (args.includes('common') && !args.includes('sortby'))
      : args.url.includes('library') ||
        (args.url.includes('common') && !args.url.includes('access'));
  const adjustedUrl = `${
    isAuth ? VITE_API_SERVER : isV3 ? VITE_API_V3 : VITE_BASE_URL
  }/${urlEnd}`;
  const adjustedArgs =
    typeof args === 'string' ? adjustedUrl : { ...args, url: adjustedUrl };
  const isLoggedIn = getLocalStorage('loggedIn');

  if (isLoggedIn === '0') {
    api.dispatch(
      setCredentials({
        accessToken: '',
        refreshToken: '',
        user: null,
      })
    );
    deleteAllTokens();
  }
  let result = await rawBaseQuery(adjustedArgs, api, extraOptions);

  if (
    result.error &&
    result.error.status === 401 &&
    isOfType(result.error.data, 'code')
  ) {
    console.log('Error: ' + result, result.error, result.error.data.code);
    if (result.error.data.code === '401_2') {
      const refreshResult: any = await rawBaseQuery(
        {
          url: `${VITE_API_SERVER}/auth/refresh`,
          method: 'POST',
          body: { data: getTokens() },
        },
        api,
        extraOptions
      );
      console.log('refreshResult : ', refreshResult);
      if (refreshResult.data.code === '200') {
        api.dispatch(setCredentials({ ...refreshResult.data.data }));
        setToken({ ...refreshResult.data.data });
        result = await rawBaseQuery(adjustedArgs, api, extraOptions);
      } else {
        deleteAllTokens();
        clearLocalStorage();
        window.location.href = `${VITE_BASE_PUBLIC_PATH}${
          VITE_BASE_PUBLIC_PATH.endsWith('/') ? '' : '/'
        }login`;
      }
    } else {
      deleteAllTokens();
      clearLocalStorage();
      if (window.location.pathname.split('/').splice(-1)[0] !== 'login') {
        window.location.href = `${VITE_BASE_PUBLIC_PATH}${
          VITE_BASE_PUBLIC_PATH.endsWith('/') ? '' : '/'
        }login`;
      }
    }
  }
  return result;
};

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: dynamicBaseQuery,
  refetchOnFocus: true,
  tagTypes: [
    'Login',
    'Main',
    'Library',
    'Share',
    'Drive',
    'Search',
    'SearchHistory',
    'Trash',
    'Student',
    'Group',
    'Teacher',
    'Settings',
  ],
  endpoints: (builder) => ({
    getTerms: builder.query({
      query: (agreeCd) => ({
        url: `library/account/agree/${agreeCd}`,
      }),
      transformResponse: (res: ResponseBody) => res.data,
    }),
    getPrevTerms: builder.query({
      query: (agreeCd) => ({
        url: `library/account/agree/${agreeCd}/prev`,
      }),
      transformResponse: (res: ResponseBody) => res.data,
    }),
    login: builder.mutation<loginResponse, Account>({
      query: (data) => ({
        url: 'auth/login',
        method: 'POST',
        body: { data },
      }),
    }),
    logout: builder.mutation({
      query: (data) => ({
        url: 'auth/logout',
        method: 'POST',
        body: { data },
      }),
    }),
    getNaverUrl: builder.query<ResponseBody, void>({
      query: () => ({
        url: 'auth/naver/url',
      }),
    }),
    getToken: builder.query({
      query: ({ code, state }) => ({
        url: 'auth/naver',
        params: { code, state },
      }),
    }),
    refreshToken: builder.mutation({
      query: (data) => ({
        url: 'auth/refresh',
        method: 'POST',
        body: { data },
      }),
    }),
    verifyEmail: builder.mutation<ValidationResult, { email: string }>({
      query: (data) => ({
        url: 'account/pwd/confirm-email',
        method: 'POST',
        body: { data },
      }),
    }),
    verifyCode: builder.mutation<ValidationResult, { email: string }>({
      query: (data) => ({
        url: 'account/pwd/cert-email',
        method: 'POST',
        body: { data },
      }),
    }),
    updatePwd: builder.mutation<
      ValidationResult,
      { email: string; pwd: string }
    >({
      query: (data) => ({
        url: 'account/pwd/submit',
        method: 'POST',
        body: { data },
      }),
    }),
    getSignupTeacherForm: builder.query<SignupFormList, void>({
      query: () => ({
        url: 'library/account/signup/teacher/init',
      }),
      transformResponse: (res: ResponseBody) => res.data,
    }),
    getSignupStudentForm: builder.query<SignupFormList, void>({
      query: () => ({
        url: 'library/account/signup/student/init',
      }),
      transformResponse: (res: ResponseBody) => res.data,
    }),
    validateTeacher: builder.mutation<ValidationResult, Credentials>({
      query: (credentials) => ({
        url: 'library/account/signup/teacher/validate',
        method: 'POST',
        body: { data: credentials },
      }),
    }),
    validateStudent: builder.mutation<ValidationResult, Credentials>({
      query: (credentials) => ({
        url: 'library/account/signup/student/validate',
        method: 'POST',
        body: { data: credentials },
      }),
    }),
    submitSignUpTeacher: builder.mutation({
      query: (data) => ({
        url: 'library/account/signup/teacher/submit',
        method: 'POST',
        body: { data },
      }),
      transformResponse: (res: ResponseBody) => res.data,
    }),
    submitSignUpStudent: builder.mutation({
      query: (data) => ({
        url: 'library/account/signup/student/submit',
        method: 'POST',
        body: { data },
      }),
      transformResponse: (res: ResponseBody) => res.data,
    }),
    getVerificationCodeForRecovery: builder.mutation({
      query: (email) => ({
        url: 'library/account/dormant/confirm-email',
        method: 'POST',
        body: { data: { email } },
      }),
      transformResponse: (res: ResponseBody) => res.data,
    }),
    verifyCodeForRecovery: builder.mutation({
      query: ({ email, certPwd }) => ({
        url: 'library/account/dormant/cert-email',
        method: 'POST',
        body: { data: { email, certPwd } },
      }),
    }),
    reactivateAccount: builder.mutation({
      query: (email) => ({
        url: 'library/account/dormant/release',
        method: 'POST',
        body: { data: { email } },
      }),
    }),
    setAccountInquiry: builder.mutation({
      query: ({ title, content, userId }) => ({
        url: 'library/account/suspension',
        method: 'POST',
        body: { data: { title, content, userId } },
      }),
      transformResponse: (res: ResponseBody) => res.data,
    }),
    getStudentCert: builder.query<any, void>({
      query: () => ({
        url: 'library/account/signup/student/cert',
      }),
    }),
    getAuthNice: builder.query({
      query: (data) => ({
        url: 'auth/nice',
        params: data,
      }),
    }),
  }),
});

export const {
  useGetTermsQuery,
  useGetPrevTermsQuery,
  useLoginMutation,
  useLogoutMutation,
  useGetNaverUrlQuery,
  useGetTokenQuery,
  useRefreshTokenMutation,
  useVerifyEmailMutation,
  useVerifyCodeMutation,
  useUpdatePwdMutation,
  useGetSignupTeacherFormQuery,
  useGetSignupStudentFormQuery,
  useValidateTeacherMutation,
  useValidateStudentMutation,
  useSubmitSignUpTeacherMutation,
  useSubmitSignUpStudentMutation,
  useGetVerificationCodeForRecoveryMutation,
  useVerifyCodeForRecoveryMutation,
  useReactivateAccountMutation,
  useSetAccountInquiryMutation,
  useGetStudentCertQuery,
  useGetAuthNiceQuery,
} = apiSlice;
